// "How To" tab content for the Settings page — pure presentational sections.

function HowToManagingUsers() {
  return (
    <section>
      <h2 className="text-lg font-semibold mb-3">Managing Users</h2>
      <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
        <li>
          Go to the <strong className="text-foreground">User Management</strong> tab.
        </li>
        <li>
          Click <strong className="text-foreground">Add</strong> to create a new user — enter their
          first name, last name, email, role (Admin or Viewer), and assign them to one or more
          groups.
        </li>
        <li>
          To edit a user, click their row in the user list. You can update their name, role, and
          group memberships.
        </li>
        <li>
          To remove a user, click the trash icon next to their name. This removes their access to
          all dashboards in this tenant.
        </li>
        <li>
          Use <strong className="text-foreground">Upload CSV</strong> to bulk-import users. The CSV
          must include columns: <code className="bg-muted px-1 rounded text-xs">email</code>,{" "}
          <code className="bg-muted px-1 rounded text-xs">first_name</code>,{" "}
          <code className="bg-muted px-1 rounded text-xs">last_name</code>. Optionally include a{" "}
          <code className="bg-muted px-1 rounded text-xs">group</code> column to auto-assign users
          to an existing group.
        </li>
      </ol>
    </section>
  );
}

function HowToManagingGroups() {
  return (
    <section>
      <h2 className="text-lg font-semibold mb-3">Managing User Groups</h2>
      <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
        <li>
          In the <strong className="text-foreground">User Management</strong> tab, find the{" "}
          <strong className="text-foreground">User Groups</strong> section.
        </li>
        <li>
          Click <strong className="text-foreground">New Group</strong> to create a group.
        </li>
        <li>Expand a group to manage its members and assign dashboards to it.</li>
        <li>
          All users in a group automatically get access to the dashboards assigned to that group.
        </li>
        <li>
          Mark a group as <strong className="text-foreground">Default</strong> so that new users
          (including those from pre-permissioned domains) are automatically added to it.
        </li>
      </ol>
    </section>
  );
}

function HowToAddingDashboards() {
  return (
    <section>
      <h2 className="text-lg font-semibold mb-3">Adding Dashboards</h2>
      <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
        <li>
          From the home page, click <strong className="text-foreground">Add Dashboard</strong>.
        </li>
        <li>Enter a title, select a category and school year, and paste the dashboard URL.</li>
        <li>
          Make sure embedding is enabled in Looker Studio: open the report →{" "}
          <em>File → Embed report</em> → toggle{" "}
          <strong className="text-foreground">Enable embedding</strong>.
        </li>
        <li>
          For the URL, copy the Looker Studio report URL from your browser&apos;s address bar (e.g.{" "}
          <code className="bg-muted px-1 rounded text-xs break-all">
            https://lookerstudio.google.com/reporting/REPORT_ID/page/PAGE_ID
          </code>
          ).
        </li>
        <li>
          After creating the dashboard, assign it to user groups so the right users can see it.
        </li>
      </ol>
    </section>
  );
}

function HowToTroubleshooting() {
  return (
    <section>
      <h2 className="text-lg font-semibold mb-3">Troubleshooting</h2>
      <div className="space-y-3 text-sm text-muted-foreground">
        <div>
          <p className="font-medium text-foreground">
            Dashboard shows &quot;Refused to connect&quot;
          </p>
          <p>
            Embedding is not enabled in Looker Studio. Open the report →{" "}
            <em>File → Embed report</em> → toggle Enable embedding on.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground">Dashboard shows &quot;You need access&quot;</p>
          <p>
            The viewer&apos;s Google account doesn&apos;t have permission. In Looker Studio, share
            the report with the appropriate users or set link sharing to &quot;Anyone with the link
            can view.&quot;
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground">User can&apos;t see a dashboard</p>
          <p>
            Make sure the user belongs to a group that has the dashboard assigned. Check their group
            memberships by clicking their name in the user list.
          </p>
        </div>
      </div>
    </section>
  );
}

export function SettingsHowTo() {
  return (
    <div className="max-w-2xl space-y-8">
      <HowToManagingUsers />
      <HowToManagingGroups />
      <HowToAddingDashboards />
      <HowToTroubleshooting />
    </div>
  );
}
